import { lazy, Suspense, useEffect, useState } from 'react';
import { useStore } from '@nanostores/react';
import { isCartOpen } from '@agentic/magento-client/stores/cart';
import { isQuoteDrawerOpen } from '@agentic/magento-client/stores/quote';
import { isSearchModalOpen } from '@agentic/magento-client/stores/search';
import { isFinderOpen } from '@agentic/magento-client/stores/finder';
import { isAuthModalOpen } from '../account/authModalStore';
import type { FinderChain } from '@agentic/magento-client/lib/finder-shared';

const CartDrawer = lazy(() => import('../cart/CartDrawer'));
const QuoteDrawer = lazy(() => import('../cart/QuoteDrawer'));
const AuthModal = lazy(() => import('../account/AuthModal'));
const SearchModal = lazy(() => import('../search/SearchModal'));
const FinderModal = lazy(() => import('../finder/FinderModal'));

// Retain a panel after its first open, preserving fields and existing effects
// across close/reopen. Its module is never requested while still unused.
function useOpened(open: boolean): boolean {
  const [opened, setOpened] = useState(false);
  useEffect(() => { if (open) setOpened(true); }, [open]);
  return open || opened;
}

export default function StorefrontOverlays({ finder }: { finder?: { chains: FinderChain[]; rememberEnabled?: boolean } }) {
  const cart = useOpened(useStore(isCartOpen));
  const quote = useOpened(useStore(isQuoteDrawerOpen));
  const auth = useOpened(useStore(isAuthModalOpen));
  const search = useOpened(useStore(isSearchModalOpen));
  const finderOpened = useOpened(useStore(isFinderOpen));
  return <Suspense fallback={<span className="sr-only" role="status">Loading panel…</span>}>
    {cart && <CartDrawer />}
    {quote && <QuoteDrawer />}
    {auth && <AuthModal />}
    {search && <SearchModal />}
    {finder && finderOpened && <FinderModal chains={finder.chains} rememberEnabled={finder.rememberEnabled} />}
  </Suspense>;
}
